import { hc } from "hono/client";
import type { AppType } from "@pulso/api";
import { supabase } from "@/lib/supabase";

async function getAuthHeaders(): Promise<HeadersInit> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// Typed client — routes are exposed at /v1/* on the server
export const client = hc<AppType>(`${import.meta.env.VITE_API_BASE_URL as string}/v1`);

class ApiClient {
  private baseUrl = import.meta.env.VITE_API_BASE_URL;

  private async handleResponse<T>(res: Response): Promise<T> {
    if (res.status === 401) {
      await supabase.auth.signOut();
      window.location.href = "/login";
      throw new Error("Sesja wygasła");
    }
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      let message = text || `Błąd ${res.status}`;
      try {
        const json = JSON.parse(text) as unknown;
        if (json && typeof json === "object") {
          const obj = json as Record<string, unknown>;
          // Zod validation error from @hono/zod-openapi
          if (obj.error && typeof obj.error === "object") {
            const err = obj.error as Record<string, unknown>;
            if (Array.isArray(err.issues)) {
              const issues = err.issues as Array<{ path?: unknown[]; message?: string }>;
              message = issues
                .map((i) => {
                  const path =
                    Array.isArray(i.path) && i.path.length > 0 ? `${i.path.join(".")}: ` : "";
                  return `${path}${i.message ?? "Błąd walidacji"}`;
                })
                .join("\n");
            } else if (typeof err.message === "string") {
              message = err.message;
            }
          } else if (typeof obj.error === "string") {
            message = obj.error;
          } else if (typeof obj.message === "string") {
            message = obj.message;
          }
        }
      } catch {
        // not JSON — use raw text
      }
      throw new Error(message);
    }
    if (res.status === 204) return undefined as T;
    return res.json();
  }

  async get<T>(path: string, params?: Record<string, string>): Promise<T> {
    const url = new URL(this.baseUrl + path);
    if (params) {
      Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
    }
    const res = await fetch(url.toString(), {
      headers: await getAuthHeaders(),
    });
    return this.handleResponse<T>(res);
  }

  async put<T>(path: string, body: unknown): Promise<T> {
    const res = await fetch(this.baseUrl + path, {
      method: "PUT",
      headers: await getAuthHeaders(),
      body: JSON.stringify(body),
    });
    return this.handleResponse<T>(res);
  }

  async post<T>(path: string, body: unknown): Promise<T> {
    const res = await fetch(this.baseUrl + path, {
      method: "POST",
      headers: await getAuthHeaders(),
      body: JSON.stringify(body),
    });
    return this.handleResponse<T>(res);
  }

  async delete(path: string): Promise<void> {
    const res = await fetch(this.baseUrl + path, {
      method: "DELETE",
      headers: await getAuthHeaders(),
    });
    await this.handleResponse<void>(res);
  }

  async patch<T>(path: string, body?: unknown): Promise<T> {
    const res = await fetch(this.baseUrl + path, {
      method: "PATCH",
      headers: await getAuthHeaders(),
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    return this.handleResponse<T>(res);
  }

  async upload<T>(path: string, formData: FormData): Promise<T> {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    const res = await fetch(this.baseUrl + path, {
      method: "POST",
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: formData,
    });
    return this.handleResponse<T>(res);
  }
}

export const api = new ApiClient();
